/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { RefObject } from 'preact';
import { ReferenceLine } from '#utils/UNSAFE_meterUtils';
import { xUnits } from '#UNSAFE_Theme/utils';
import { meterBarVars } from './themes/MeterBarContract.css';
import { Size, sizeToCSS } from '#utils/UNSAFE_size';

const FIT_REF_BAR_PROTRUSION = 16;

const MAX_VERTICAL_LABEL_WIDTH_IN_XUNITS = 15;

const REF_LINE_LENGTH_IN_XUNITS = 5;

const LABEL_GAP = 4;

type Dimensions = {
  width: number;
  height: number;
};

/**
 * Returns the metric value of the clicked point.
 * @param event The PointerEvent
 * @param dimensionsRef RefObject to stored dimensions of the meter bar.
 * @param min The minimum value of the meter bar.
 * @param max The maximum value of the meter bar.
 * @param step The step value of the meter bar.
 * @param orientation The orientation of the meter bar.
 * @param isRtl Whether the reading mode is 'rtl'
 * @returns The metric value of the clicked point.
 */
export function getValue(
  event: PointerEvent,
  dimensionsRef: RefObject<Dimensions>,
  min: number,
  max: number,
  step: number,
  orientation?: 'horizontal' | 'vertical',
  isRtl?: boolean
): number | undefined {
  const dims = dimensionsRef.current;
  if (!dims || dims.width === 0) {
    return undefined;
  }

  let val;

  if (orientation === 'horizontal') {
    const width = event.offsetX;
    val = min + ((max - min) * (isRtl ? dims.width - width : width)) / dims.width;
  } else {
    const height = dims.height - event.offsetY;
    val = min + ((max - min) * height) / dims.height;
  }
  val = Math.round(val / step) * step;
  return Math.max(min, Math.min(max, val));
}

/**
 * Returns the true dimension of the meter bar after first render. All dimensions are 0 for initial render.
 * @param element The meter bar track HTML element
 * @returns The dimension of the meter bar.
 */
export function getDimensions(element: HTMLElement | null): Dimensions {
  if (!element) {
    return {
      width: 0,
      height: 0
    };
  }
  const trackDims = element.getBoundingClientRect();

  return {
    width: trackDims.width,
    height: trackDims.height
  };
}

/**
 * Returns object with 'start' and 'end' keys. Value for each key will be true if at least one ref line with correposnding position is present in meter bar.
 * @param lines The array of reference lines.
 * @returns Returns object with start and end keys.
 */
export function getRefLinesInfo(lines?: ReferenceLine[]): {
  start: boolean;
  end: boolean;
} {
  let refLineInfo = { start: false, end: false };
  if (lines) {
    refLineInfo = lines.reduce((acc, cur) => {
      acc.start = acc.start || cur.position === 'start';
      acc.end = acc.end || cur.position === 'end' || cur.position == null;
      return acc;
    }, refLineInfo);
  }
  return refLineInfo;
}

export const getMarginClass = function (
  info: { start: boolean; end: boolean },
  isHoriz: boolean,
  barTrackMarginTopStyle: string,
  barTrackMarginStartStyle: string,
  barTrackMarginBottomStyle: string,
  barTrackMarginEndStyle: string
) {
  const className = [];

  if (info.start) {
    className.push(isHoriz ? barTrackMarginTopStyle : barTrackMarginStartStyle);
  }

  if (info.end) {
    className.push(isHoriz ? barTrackMarginBottomStyle : barTrackMarginEndStyle);
  }
  return className;
};

export const getFitMarginClass = function (
  info: { start: boolean; end: boolean },
  isHoriz: boolean,
  barTrackMarginTopFitStyle: string,
  barTrackMarginStartFitStyle: string,
  barTrackMarginBottomFitStyle: string,
  barTrackMarginEndFitStyle: string
) {
  const className = [];

  if (info.start) {
    className.push(isHoriz ? barTrackMarginTopFitStyle : barTrackMarginStartFitStyle);
  }

  if (info.end) {
    className.push(isHoriz ? barTrackMarginBottomFitStyle : barTrackMarginEndFitStyle);
  }
  return className;
};

export const getFitTrackWidth = function (
  info: { start: boolean; end: boolean },
  maxLabelWidth: number,
  width?: number,
  isHoriz?: boolean
) {
  if (width) {
    if (info.start && !isHoriz) {
      width = width - FIT_REF_BAR_PROTRUSION;
    }

    if (info.end && !isHoriz) {
      width = width - FIT_REF_BAR_PROTRUSION;
    }
  }

  if (maxLabelWidth && width) {
    width -= 2 * maxLabelWidth + 2 * LABEL_GAP;
  }

  return width;
};

export const getFitTrackHeight = function (
  info: { start: boolean; end: boolean },
  maxLabelHeight: number,
  height?: number,
  isHoriz?: boolean
) {
  if (height) {
    if (info.start && isHoriz) {
      height = height - FIT_REF_BAR_PROTRUSION;
    }
    if (info.end && isHoriz) {
      height = height - FIT_REF_BAR_PROTRUSION;
    }
  }

  if (maxLabelHeight && height) {
    height -= 2 * maxLabelHeight + 2 * LABEL_GAP;
  }
  return height;
};

export const getFitMarginStyle = function (
  maxLabelWidth: number,
  maxLabelHeight: number,
  isHoriz: boolean
) {
  const refMargin = `${xUnits(4)} + ${LABEL_GAP}px`;
  if (isHoriz) {
    return `calc(${refMargin} + ${maxLabelHeight}px) 0 calc(${refMargin} + ${maxLabelHeight}px) 0`;
  }

  return `0 calc(${refMargin} + ${maxLabelWidth}px) 0 calc(${refMargin} + ${maxLabelWidth}px)`;
};

/**
 * Returns the translation offset for the reference line label
 */
export function getReferenceLineLabelTranslationOffset(
  isLeft: boolean,
  isBottom: boolean,
  isHoriz: boolean,
  isRtl: boolean,
  isStartPos: boolean,
  length?: string,
  size?: string
) {
  const refLineLength = length || xUnits(REF_LINE_LENGTH_IN_XUNITS);
  const barSize = size || meterBarVars.size;
  const overHang = `calc(${refLineLength} - ${barSize})`;

  if (isHoriz) {
    if (isStartPos) {
      return `${isLeft ? '-50%' : '50%'} calc(-100% - ${overHang} - ${LABEL_GAP}px)`;
    } else {
      return `${isLeft ? '-50%' : '50%'} calc(${refLineLength} + ${LABEL_GAP}px)`;
    }
  } else {
    if (isRtl) {
      return isStartPos
        ? `calc(100% + ${overHang} + ${LABEL_GAP}px) ${isBottom ? '50%' : '-50%'}`
        : `calc(0px - ${refLineLength} - ${LABEL_GAP}px) ${isBottom ? '50%' : '-50%'}`;
    } else {
      return isStartPos
        ? `calc(-100% - ${overHang} - ${LABEL_GAP}px) ${isBottom ? '50%' : '-50%'}`
        : `calc(${refLineLength} + ${LABEL_GAP}px) ${isBottom ? '50%' : '-50%'}`;
    }
  }
}

/**
 * Returns the margin needed to accomodate reference line and label in a sized meter circle.
 */
export function getSizedMargin(
  info: { start: boolean; end: boolean },
  isHoriz: boolean,
  hasReferenceLine: boolean,
  hasReferenceLineLabel: boolean,
  maxRefLabelDims: { width: number; height: number }
) {
  const marginValue = `calc(${xUnits(REF_LINE_LENGTH_IN_XUNITS)} - ${meterBarVars.size})`;

  const gap = LABEL_GAP;

  const hasBothPosition = info.start && info.end;

  if (hasReferenceLineLabel) {
    return isHoriz
      ? `calc(${maxRefLabelDims.height + gap}px + ${marginValue}) 0 calc(${
          maxRefLabelDims.height + gap
        }px + ${marginValue}) 0`
      : `0 calc(${maxRefLabelDims.width + gap}px + ${marginValue}) 0 calc(${
          maxRefLabelDims.width + gap
        }px + ${marginValue})`;
  } else if (hasReferenceLine) {
    if (hasBothPosition) {
      return isHoriz ? `${marginValue} 0 ${marginValue}` : `0 ${marginValue} 0 ${marginValue}`;
    } else if (info.start && !info.end) {
      return isHoriz ? `${marginValue} 0 0 0` : `0 ${marginValue} 0 0`;
    } else if (!info.start && info.end) {
      return isHoriz ? `0 0 ${marginValue} 0` : `0 0 0 ${marginValue}`;
    }
  }
  return undefined;
}

export function getComponentStyle(
  isFit: boolean,
  isHoriz: boolean,
  length: Size,
  maxRefLabelDims: { width: number; height: number },
  fitTrackHeight?: number,
  fitTrackWidth?: number
) {
  return {
    width: isFit ? fitTrackWidth : isHoriz ? sizeToCSS(length) : undefined,
    height: isFit ? fitTrackHeight : isHoriz ? undefined : sizeToCSS(length),
    margin: isFit
      ? getFitMarginStyle(maxRefLabelDims.width, maxRefLabelDims.height, isHoriz)
      : undefined
  };
}

export function getReferenceLineAlignmentStyle(
  isLeft: boolean,
  isRtl: boolean,
  maxLabelWidth: number,
  percentage: number
) {
  return {
    left: isLeft ? `max(${maxLabelWidth}px, ${isRtl ? 100 - percentage : percentage}%)` : undefined,
    right: !isLeft
      ? `max(${maxLabelWidth}px, ${isRtl ? percentage : 100 - percentage}%)`
      : undefined
  };
}

export function getReferenceLineTranslation(isHoriz: boolean, isRtl: boolean, percent: number) {
  if (percent === 0 && !isRtl) {
    return;
  }
  if (percent === 100) {
    if (isRtl) return isHoriz ? '3px' : '0 3px';
    return isHoriz ? '-2px' : '0 2px';
  }
  return isHoriz ? (isRtl ? '1px 0' : '-1px 0') : isRtl ? '0 -1px' : '0 1px';
}

export function getReferenceLinePosition(
  isHoriz: boolean,
  isLeft: boolean,
  isBottom: boolean,
  isRtl: boolean,
  percent: number,
  maxLabelWidth: number,
  maxLabelHeight: number
) {
  return {
    ...(isHoriz ? getReferenceLineAlignmentStyle(isLeft, isRtl, maxLabelWidth / 2, percent) : {}),
    bottom: isHoriz
      ? undefined
      : isBottom
      ? `max(${maxLabelHeight / 2 - LABEL_GAP}px, ${percent}%)`
      : undefined,
    top: isHoriz
      ? undefined
      : !isBottom
      ? `max(${maxLabelHeight / 2 - LABEL_GAP}px, ${100 - percent}%)`
      : undefined,
    maxWidth: !isHoriz ? xUnits(MAX_VERTICAL_LABEL_WIDTH_IN_XUNITS) : undefined
  };
}
